// Core Modules
import { InjectionToken } from '@angular/core';

// Models
import { AssistantHelpCategory, AssistantMessage } from './ignite-assistant.model';

/**
 * Which optional surfaces of the assistant panel are available. The docs
 * assistant and the IGP assistant may differ here, so the component reads
 * them rather than hardcoding them.
 */
export interface AssistantFeatureFlags {
  /** The saved-conversation flyout and its "Chat history" menu entry. */
  history: boolean;
  /** Thumbs up/down on a reply. */
  feedback: boolean;
  /** The grouped example-questions view and its "Help" menu entry. */
  help: boolean;
}

/**
 * Everything that differs between one deployment of the assistant panel and
 * another: its copy, which backend endpoint it talks to, and which optional
 * surfaces it offers.
 *
 * Anything shared by every assistant — history page size, input length cap,
 * skeleton row count, title truncation — stays a constant in
 * `ignite-assistant.data.ts`. Only what actually varies belongs here.
 */
export interface AssistantConfig {
  /** Panel header text. */
  title: string;
  /** Opening bot message shown on a fresh conversation. */
  greeting: AssistantMessage;
  /** Suggestion chips shown before the first question. */
  presetQuestions: readonly string[];
  /** Example questions in the help view, grouped by capability. */
  helpCategories: readonly AssistantHelpCategory[];
  /** Composer placeholder while idle. */
  inputPlaceholder: string;
  /** Composer placeholder while a reply is in flight. */
  waitingPlaceholder: string;
  /** Bot message shown when a request to the assistant API fails. */
  errorMessage: string;
  /** Which `appEndpoints` entry to POST a turn to. */
  chatEndpointKey: 'chat' | 'chatDocs';
  /**
   * Which product this assistant belongs to, sent as `?product=` when listing
   * history. Several products can share one backend, so this is what keeps one
   * product's conversations out of another's.
   */
  product: string;
  /**
   * Which assistant within that product, sent as `?assistant=`. Together with
   * `product` it names a row in the backend's assistant registry; an
   * unregistered pair is rejected rather than returning an empty list.
   */
  assistant: string;
  features: AssistantFeatureFlags;
}

/**
 * Supplied at the application root for the IGP assistant, and overridden
 * on the docs route.
 *
 * A route-level override only reaches `IgniteAssistantService` if the service
 * is ALSO listed in that route's providers — it is `providedIn: 'root'`, and a
 * root-provided instance resolves the root token. See the docs route in `app.routes.ts`.
 */
export const ASSISTANT_CONFIG = new InjectionToken<AssistantConfig>('ASSISTANT_CONFIG');
